// src/ChatInput.tsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  makeStyles,
  Textarea,
  Button,
  tokens,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Toast,
  Toaster,
  ToastTitle,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  Send20Regular,
  BinRecycle20Regular,
  Mic20Regular,
  Stop20Regular,
  Dismiss20Regular,
  SpinnerIos20Regular,
} from "@fluentui/react-icons";
import { getBrandConfig } from '../brands/brand-registry';
import { getAppConfig } from '../api/api';
import { useMicrophone } from '../hooks/useMicrophone';
import { useLiveSpeech } from '../hooks/useLiveSpeech';
import { InputMode } from '../api/models';
import { stopAllAudioPlayback } from '../services/audioService';
import { appendTranscriptValue, appendVoiceTranscript } from '../utils/voiceDraft';
import { InterpolatedPhoneText } from './InterpolatedPhoneText';

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    maxWidth: '1000px',
    width: '95%',
    margin: '0 auto',
    marginBottom: '40px',
    backgroundColor: 'var(--chat-input-bg, var(--neutral-0))',
    borderRadius: "var(--radius-field, 10px)",
    border: `1px solid var(--chat-input-border, var(--border-primary))`,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    boxShadow: "var(--chat-input-shadow, none)",
    '@media (max-width: 600px)': {
      width: '92%',
      padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
      marginBottom: 0,
      paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)', /* Safe area for touch assistance */
    },
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    width: "100%",
    '@media (max-width: 600px)': {
      gap: tokens.spacingHorizontalXS,
    },
  },
  textarea: {
    flexGrow: 1,
    minHeight: "var(--chat-input-min-height, 48px)",
    maxHeight: "var(--chat-input-max-height, 120px)",
    resize: "none",
    backgroundColor: "transparent",
    borderRadius: "0",
    border: "none !important",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    boxShadow: "none",
    '&::placeholder': {
      color: 'var(--chat-input-placeholder-color, #595959)',
      opacity: 1,
    },
    '& textarea::placeholder': {
      color: 'var(--chat-input-placeholder-color, #595959)',
      opacity: 1,
    },
    '& textarea:disabled::placeholder': {
      color: 'var(--chat-input-placeholder-color, #595959)',
      opacity: 1,
    },
    '@media (max-width: 600px)': {
      fontSize: 'var(--welcome-input-font-size-mobile, 14px)',
      lineHeight: 'var(--welcome-input-line-height-mobile, 20px)',
      padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    },
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    flexShrink: 0,
    '@media (max-width: 600px)': {
      gap: tokens.spacingVerticalXS,
    }
  },
  micButtonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
    gap: tokens.spacingVerticalXXS,
    '@media (max-width: 600px)': {
      gap: tokens.spacingVerticalXXS,
    }
  },
  recordingMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXS,
    minHeight: '14px',
  },
  recordingTimer: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    minHeight: '14px',
    lineHeight: '14px',
    '@media (max-width: 600px)': {
      fontSize: '9px',
      minHeight: '12px',
      lineHeight: '12px',
    },
  },
  recordingStatus: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    minHeight: '14px',
    lineHeight: '14px',
    '@media (max-width: 600px)': {
      fontSize: '9px',
      minHeight: '12px',
      lineHeight: '12px',
    },
  },
  cancelRecordingButton: {
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorStatusDangerForeground1,
    minWidth: '24px',
    minHeight: '24px',
    width: '24px',
    height: '24px',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      color: tokens.colorStatusDangerForeground2,
      backgroundColor: tokens.colorStatusDangerBackground1,
      borderRadius: '9999px',
    },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: '9999px',
    },
  },
  inlineClearInputButton: {
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    minWidth: '30px',
    minHeight: '30px',
    width: '30px',
    height: '30px',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground3,
      borderRadius: '9999px',
    },
    '&:focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      borderRadius: '9999px',
    },
  },
  ctaPrimary: {
    background: "var(--brand-60)",
    color: "var(--brand-send-btn-text, #FFFFFF)",
    borderRadius: "var(--radius-full, 9999px)",
    border: "none",
    minWidth: "44px",
    minHeight: "44px",
    width: "44px",
    height: "44px",
    padding: 0,
    "&:hover": {
      background: "var(--brand-30)",
    },
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  clearButton: {
    borderRadius: "var(--radius-full, 9999px)",
    border: "none",
    backgroundColor: tokens.colorStatusWarningBackground2,
    color: tokens.colorNeutralForeground1,
    minWidth: "44px",
    minHeight: "44px",
    width: "44px",
    height: "44px",
    padding: 0,
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground5,
    },
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  micButtonConnecting: {
    backgroundColor: tokens.colorNeutralBackground4,
    cursor: "wait",
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  micButton: {
    borderRadius: "var(--radius-full, 9999px)",
    border: "2px solid var(--brand-60, #0078d4)",
    backgroundColor: "var(--brand-160, #f0f6ff)",
    color: "var(--brand-60, #0078d4)",
    minWidth: "44px",
    minHeight: "44px",
    width: "44px",
    height: "44px",
    padding: 0,
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground4,
    },
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  micButtonRecording: {
    backgroundColor: tokens.colorStatusDangerBackground2,
    color: tokens.colorNeutralForegroundOnBrand,
    animation: "pulse-recording 1s ease-in-out infinite",
    "&:hover": {
      backgroundColor: tokens.colorStatusDangerBackground3,
    },
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  micButtonProcessing: {
    backgroundColor: tokens.colorNeutralBackground4,
    cursor: "wait",
    '@media (max-width: 600px)': {
      minWidth: '36px',
      minHeight: '36px',
      width: '36px',
      height: '36px',
    },
  },
  disclaimer: {
    fontSize: "11px",
    lineHeight: "1.3",
    color: "var(--disclaimer-text-color, #666)",
    margin: 0,
    textAlign: "center" as const,
    '@media (max-width: 600px)': {
      fontSize: 'var(--disclaimer-font-size-mobile, 11px)',
      lineHeight: 'var(--disclaimer-line-height-mobile, 1.2)',
    },
  },
  voiceUnavailable: {
    color: 'var(--text-secondary, #424242)',
    fontSize: '12px',
    lineHeight: '1.4',
    margin: 0,
    textAlign: 'left' as const,
  },
  disclaimerLink: {
    color: "var(--disclaimer-link-color, var(--brand-60))",
    fontWeight: 600,
    textDecoration: "underline",
    "&:hover": {
      color: "var(--disclaimer-link-hover-color, var(--brand-40))",
    },
    '@media (max-width: 600px)': {
      fontSize: 'var(--disclaimer-link-size-mobile, 12px)',
    },
  },
});

interface Props {
  value: string;
  onChange: (val: string) => void;
  onSend: (message: string, briefResponse: boolean, inputMode?: InputMode) => void;
  onVoiceTranscription?: (text: string) => void;
  onClearContext: () => void;
  autoFocus?: boolean; // Optional prop to control auto-focus behavior
  isRateLimited?: boolean; // Rate limit state
  disabled?: boolean; // Disable input
}

export const ChatInput: React.FC<Props> = ({
  value,
  onChange,
  onSend,
  onVoiceTranscription,
  onClearContext,
  autoFocus = false,
  isRateLimited = false,
  disabled = false,
}) => {
  const styles = useStyles();
  const brandConfig = getBrandConfig();
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);
  const voiceDraftOriginalRef = useRef<string | null>(null);
  const hasVoiceInputRef = useRef(false);
  const preRecordingValueRef = useRef<string>('');

  const isDisabled = disabled || isRateLimited;
  const [srAnnouncement, setSrAnnouncement] = useState<string>('');
  const voiceToasterId = useId('voice-input-toaster');
  const { dispatchToast } = useToastController(voiceToasterId);
  const isMicSupported = typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined';
  
  // Get speech provider from brand config
  const speechProvider = (brandConfig.features as { speechProvider?: 'openai' | 'abair' }).speechProvider || 'openai';
  const abairWebSocketUrl = (brandConfig.features as { abairWebSocketUrl?: string }).abairWebSocketUrl || '';
  const isAbairProvider = speechProvider === 'abair' && abairWebSocketUrl;
  const sttSilenceTimeoutMs = getAppConfig().stt_silence_timeout_ms ?? 3000;
  const voiceInputLabels = brandConfig.chatInput.voiceInput;

  const showNoSpeechToast = (message: string) => {
    dispatchToast(
      <Toast>
        <ToastTitle>{message}</ToastTitle>
      </Toast>,
      { intent: 'warning', timeout: 5000 },
    );
  };

  const applyVoiceTranscript = (text: string) => {
    const result = appendVoiceTranscript(preRecordingValueRef.current, text, {
      hasVoiceInput: hasVoiceInputRef.current,
      pureVoiceValue: voiceDraftOriginalRef.current,
    });
    onChange(result.value);
    onVoiceTranscription?.(text);
    hasVoiceInputRef.current = result.state.hasVoiceInput;
    voiceDraftOriginalRef.current = result.state.pureVoiceValue;
  };

  // Live speech hook for Abair (real-time text display)
  const liveSpeech = useLiveSpeech({
    provider: 'abair',
    websocketUrl: abairWebSocketUrl,
    onFinalTranscript: applyVoiceTranscript,
    onError: (error) => {
      setSrAnnouncement(error);
    },
    onAnnounce: (message) => {
      setSrAnnouncement(message);
    },
    messages: voiceInputLabels,
  });

  // Batch microphone hook for OpenAI (traditional flow)
  const batchMicrophone = useMicrophone({
    onTranscription: applyVoiceTranscript,
    onError: (error) => {
      if (error === voiceInputLabels.noSpeechDetectedMessage) {
        setSrAnnouncement('');
        showNoSpeechToast(error);
        return;
      }
      setSrAnnouncement(error);
    },
    onAnnounce: (message) => {
      setSrAnnouncement(message);
    },
    silenceTimeoutMs: sttSilenceTimeoutMs,
    messages: voiceInputLabels,
  });

  // Unified interface for both providers
  const micState = isAbairProvider ? liveSpeech.state : batchMicrophone.micState;
  const timerDisplay = isAbairProvider ? liveSpeech.timerDisplay : batchMicrophone.timerDisplay;
  const partialText = isAbairProvider ? liveSpeech.partialText : '';
  const recordingStatusText = micState === 'connecting'
    ? voiceInputLabels.preparingStatus
    : micState === 'recording'
      ? voiceInputLabels.listeningStatus
      : '';
  const isVoiceInputLocked = micState === 'connecting' || micState === 'recording' || micState === 'processing';
  const isMicTemporarilyUnavailable = micState === 'connecting' || micState === 'processing';
  
  const toggleRecording = useCallback(() => {
    if (micState === 'idle') {
      setSrAnnouncement('');
      preRecordingValueRef.current = value;
    }

    if (isAbairProvider) {
      if (liveSpeech.isRecording) {
        liveSpeech.stopRecording();
      } else if (liveSpeech.state === 'idle') {
        liveSpeech.startRecording();
      }
    } else {
      batchMicrophone.toggleRecording();
    }
  }, [
    batchMicrophone.toggleRecording,
    isAbairProvider,
    liveSpeech.isRecording,
    liveSpeech.startRecording,
    liveSpeech.state,
    liveSpeech.stopRecording,
    micState,
    value,
  ]);
  
  const cancelRecording = useCallback(() => {
    if (isAbairProvider) {
      liveSpeech.cancelRecording();
    } else {
      batchMicrophone.cancelRecording();
    }
    onChange(preRecordingValueRef.current);
  }, [batchMicrophone.cancelRecording, isAbairProvider, liveSpeech.cancelRecording, onChange]);

  useEffect(() => {
    if (micState !== 'recording' && micState !== 'connecting') {
      return;
    }

    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelRecording();
      }
    };

    window.addEventListener('keydown', onWindowKeyDown);
    return () => window.removeEventListener('keydown', onWindowKeyDown);
  }, [cancelRecording, micState]);

  useEffect(() => {
    const onShortcutKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key.toLowerCase() === 'm' && !event.repeat && isMicSupported && !isDisabled) {
        event.preventDefault();
        toggleRecording();
      }
    };

    window.addEventListener('keydown', onShortcutKeyDown);
    return () => window.removeEventListener('keydown', onShortcutKeyDown);
  }, [isDisabled, isMicSupported, toggleRecording]);

  // Update input with partial text during live recording
  useEffect(() => {
    if (isAbairProvider && liveSpeech.isRecording && partialText) {
      onChange(appendTranscriptValue(preRecordingValueRef.current, partialText));
    }
  }, [isAbairProvider, liveSpeech.isRecording, partialText, onChange]);

  // Auto-focus on initial load if requested (for welcome screen)
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      // Small delay to ensure component is fully rendered
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);
  
  const handleSend = () => {
    if (isVoiceInputLocked) {
      return;
    }
    if (value.trim()) {
      const trimmed = value.trim();
      // Get brief response setting from localStorage only if feature is enabled
      let briefResponse = false;
      if (brandConfig.features.enableBriefResponse) {
        const storedBrief = localStorage.getItem('briefResponse');
        briefResponse = storedBrief !== null && storedBrief === 'true';
      }
      let inputMode: InputMode = 'Text';
      if (hasVoiceInputRef.current) {
        inputMode = voiceDraftOriginalRef.current === trimmed ? 'Voice' : 'Voice_edited';
      }
      onSend(trimmed, briefResponse, inputMode);
      voiceDraftOriginalRef.current = null;
      hasVoiceInputRef.current = false;
      setSrAnnouncement('');
      onChange("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Cancel recording on Escape
    if (e.key === "Escape" && (micState === 'recording' || micState === 'connecting')) {
      e.preventDefault();
      e.stopPropagation();
      cancelRecording();
    }
  };

  const handleClearClick = () => {
    setClearDialogOpen(true);
  };

  const closeClearDialogAndRestoreFocus = () => {
    setClearDialogOpen(false);
    requestAnimationFrame(() => {
      if (clearButtonRef.current?.isConnected) {
        clearButtonRef.current.focus();
      }
    });
  };

  const handleConfirmClear = () => {
    if (isVoiceInputLocked) {
      return;
    }
    stopAllAudioPlayback();
    voiceDraftOriginalRef.current = null;
    hasVoiceInputRef.current = false;
    setSrAnnouncement('');
    onClearContext();
    setClearDialogOpen(false);
  };

  const handleClearInput = () => {
    if (isVoiceInputLocked) {
      return;
    }
    voiceDraftOriginalRef.current = null;
    hasVoiceInputRef.current = false;
    setSrAnnouncement('');
    onChange("");
  };

  const handleInputChange = (nextValue: string) => {
    setSrAnnouncement('');
    if (!nextValue.trim()) {
      voiceDraftOriginalRef.current = null;
      hasVoiceInputRef.current = false;
    }
    onChange(nextValue);
  };

  const dialogConfig = brandConfig.chatInput.clearConfirmDialog;

  return (
    <>
      <Toaster toasterId={voiceToasterId} position="top-end" />
      <div className={styles.container}>
        <div className={styles.inputRow}>
          {/* Screen reader live region for announcements */}
          <span aria-live="polite" aria-atomic="true" className="sr-only">
            {srAnnouncement}
          </span>
          
          {/* Mic button on the left - conditionally rendered based on brand config */}
          {brandConfig.features.enableVoiceInput && isMicSupported && (
            <div className={styles.micButtonContainer}>
              <span aria-hidden="true" className={styles.recordingStatus}>
                {recordingStatusText}
              </span>
              <button
                className={`${styles.micButton} ${micState === 'recording' ? styles.micButtonRecording : ''} ${micState === 'processing' ? styles.micButtonProcessing : ''} ${micState === 'connecting' ? styles.micButtonConnecting : ''}`}
                onClick={toggleRecording}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!e.repeat) {
                      toggleRecording();
                    }
                  }
                }}
                disabled={isDisabled}
                aria-disabled={isDisabled || isMicTemporarilyUnavailable}
                aria-keyshortcuts="Alt+M"
                aria-label={voiceInputLabels.startRecordingAriaLabel}
              >
                {
                  micState === 'connecting' ? <SpinnerIos20Regular /> :
                  micState === 'processing' ? <SpinnerIos20Regular /> :
                  micState === 'recording' ? <Stop20Regular /> :
                  <Mic20Regular />
                }
              </button>
              <div className={styles.recordingMetaRow}>
                <span
                  aria-hidden="true"
                  className={styles.recordingTimer}
                >
                  {(micState === 'recording' || micState === 'connecting') ? timerDisplay : ''}
                </span>
                {(micState === 'recording' || micState === 'connecting') && (
                  <button
                    type="button"
                    className={styles.cancelRecordingButton}
                    onClick={cancelRecording}
                    aria-keyshortcuts="Escape"
                    aria-label={voiceInputLabels.cancelRecordingAriaLabel}
                    title={voiceInputLabels.cancelRecordingTitle}
                  >
                    <Dismiss20Regular />
                  </button>
                )}
              </div>
            </div>
          )}
          
          <Textarea
            ref={textareaRef}
            className={styles.textarea}
            placeholder={brandConfig.chatInput.placeholder}
            aria-label={brandConfig.chatInput.ariaLabel || `Type your message to ${brandConfig.appName} chatbot`}
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isDisabled}
            aria-disabled={isDisabled}
            readOnly={isVoiceInputLocked}
            maxLength={500}
          />

          {value.trim().length > 0 && !isVoiceInputLocked && (
            <button
              type="button"
              className={styles.inlineClearInputButton}
              onClick={handleClearInput}
              aria-label={voiceInputLabels.clearInputAriaLabel}
              title={voiceInputLabels.clearInputTitle}
              disabled={isDisabled || isVoiceInputLocked}
            >
              <Dismiss20Regular />
            </button>
          )}
          
          {/* Send and Clear buttons on the right */}
          <div className={styles.buttonContainer}>
            <Button
              icon={<Send20Regular />}
              appearance="primary"
              onClick={handleSend}
              className={styles.ctaPrimary}
              disabled={isDisabled || isVoiceInputLocked}
              aria-label={brandConfig.chatInput.sendButton}
            />
            <Button
              ref={clearButtonRef}
              appearance="subtle"
              icon={<BinRecycle20Regular />}
              onClick={handleClearClick}
              className={styles.clearButton}
              disabled={isDisabled || isVoiceInputLocked}
              aria-label={brandConfig.chatInput.clearButton}
            />
          </div>
        </div>
        {brandConfig.features.enableVoiceInput && !isMicSupported && (
          <p className={styles.voiceUnavailable} role="status">
            {voiceInputLabels.unsupportedBrowserMessage}
          </p>
        )}
        {brandConfig.hero.disclaimerText && (
          <p className={styles.disclaimer}>
            {brandConfig.hero.disclaimerText}
            {brandConfig.hero.disclaimerLinkText && brandConfig.hero.disclaimerLinkUrl && (
              <a
                href={brandConfig.hero.disclaimerLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.disclaimerLink}
              >
                {brandConfig.hero.disclaimerLinkText}
              </a>
            )}
            <InterpolatedPhoneText
              text={brandConfig.hero.disclaimerAfterLinkText}
              phoneNumber={brandConfig.hero.disclaimerPhoneNumber}
              linkClassName={styles.disclaimerLink}
            />
          </p>
        )}
      </div>

      {/* WCAG 2.1 Level AA: Confirmation dialog for destructive action */}
      <Dialog 
        open={clearDialogOpen} 
        onOpenChange={(_e, data) => {
          if (data.open) {
            setClearDialogOpen(true);
          } else {
            closeClearDialogAndRestoreFocus();
          }
        }}
      >
        <DialogSurface role="alertdialog" aria-describedby="clear-dialog-desc">
          <DialogBody>
            <DialogTitle>{dialogConfig?.title || 'Clear conversation?'}</DialogTitle>
            <DialogContent id="clear-dialog-desc">
              {dialogConfig?.description || 'This will delete your entire conversation history. This cannot be undone.'}
            </DialogContent>
            <DialogActions>
              <Button 
                appearance="secondary" 
                onClick={closeClearDialogAndRestoreFocus}
              >
                {dialogConfig?.cancelButton || 'Cancel'}
              </Button>
              <Button 
                appearance="primary" 
                onClick={handleConfirmClear}
              >
                {dialogConfig?.confirmButton || 'Clear'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
